import {
  DocumentNode,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  useQuery as _useQuery
} from "@apollo/client"
import { graphql } from "@apollo/client/react/hoc"
import get from "lodash/get"
import has from "lodash/has"
import React, {
  Component,
  ComponentType,
  ReactNode,
  useCallback,
  useMemo,
  useState
} from "react"

// Retains expected behavior of Apollo Client v2 where data is NOT reset to undefined when loading state changes.
export const useQuery = <
  TData = any,
  TVariables extends OperationVariables = OperationVariables
>(
  q: DocumentNode,
  options: QueryHookOptions<TData, TVariables> = {}
): QueryResult<TData, TVariables> => {
  const query = _useQuery<TData, TVariables>(q, options)
  query.data = query.data ?? query.previousData
  return query
}

// Recursively flatten all { edges: { node: T }[] } to T[]
// Handles optional, nullable, and union edge/node types
export type FlattenEdges<T> = T extends {
  edges?: (infer E)[] | null | undefined
}
  ? E extends {
      node?: infer N | null | undefined
    }
    ? Array<FlattenEdges<N>> & {
        pageInfo?: {
          hasNextPage?: boolean | null | undefined
          endCursor?: string | null | undefined
        }
        total?: number | null | undefined
      }
    : []
  : T extends object
  ? { [K in keyof T]: FlattenEdges<T[K]> }
  : T

// flattenEdges({ foo: { edges: [{ node: { bar: 1 } }] } })
// => { foo: [{ bar: 1 }] }
// BIG NOTE: ideally, do not use, or use sparingly. This util produces shallow clones of the nodes, which can lead to
// performance issues due to component re-renders (prev/next referential equality checks).
export function flattenEdges<T>(obj: T): FlattenEdges<T> {
  const isObject = Object.prototype.toString.call(obj) === "[object Object]"
  if (!isObject) return obj as FlattenEdges<T>
  let result: any = {}
  for (let key in obj) {
    if (key === "edges") {
      result = ((obj[key] || []) as any[]).map((edge) =>
        flattenEdges(edge.node)
      )
      // this is probably a bad idea
      // TODO: remove these, or type them properly. Currently choosing to not type them
      // so that we can update consumers incrementally, removing the dependency on these fields.
    } else if (key === "pageInfo") {
      result.pageInfo = obj[key]
    } else if (key === "total") {
      result.total = obj[key]
    } else {
      result[key] = flattenEdges(obj[key])
    }
  }
  return result
}

// util for throwing an error if the expected pageInfo path does not exist in the query data
const _errorOnNonExistyPageInfoPath = (data: any, path: string): void => {
  if (has(data, path) && !has(data, `${path}.pageInfo`)) {
    throw new Error(
      `Attempted to setup paginator for "${path}" but unable as pageInfo does not exist in the query data. Be sure to add pageInfo to your query.`
    )
  }
}

// resolves data path (related to `useQuery` and `graphql` data shape differences)
const _getDataPath = (data: any, path: string): string => {
  if (data && data.data) return `data.${path}`
  return path
}

// loadMoreAuthors: createPaginator(data, "resource.authors", "authorCursor")
// => () => promise

// NOTE: in order for the loading prop to be updated when calling `fetchMore`
// you must pass notifyOnNetworkStatusChange: true, in the GQL query options
export function createPaginator(
  data: any,
  path: string,
  cursorVar: string = "cursor"
): () => Promise<any> {
  const dataPath = _getDataPath(data, path)
  // ensure we have pageInfo
  _errorOnNonExistyPageInfoPath(data, dataPath)

  const endCursorPath = `${dataPath}.pageInfo.endCursor`

  return function () {
    return data.fetchMore({
      variables: {
        ...data.variables,
        [cursorVar]: get(data, endCursorPath)
      }
    })
  }
}
// The paginator value structure.
type PaginatorValue = {
  // We check edges for current count.
  edges?: ({ node?: any } | null)[] | null | undefined
  // Page info for pagination.
  pageInfo?:
    | {
        hasNextPage?: boolean | null | undefined
        endCursor?: string | null | undefined
      }
    | null
    | undefined
  // Total count of items.
  total?: number | null | undefined
}

// Require certain variables for pagination.
type PaginatorVariables = {
  // Number of items to fetch per page.
  first?: number | null | undefined
  // Cursor for pagination. Passed as a variable when fetching more items.
  cursor?: string | null | undefined
}

type UsePaginatorOptions<TVariables extends PaginatorVariables = any> = {
  paginatorValue: PaginatorValue | null | undefined
  query: QueryResult<any, TVariables>
  cursorKey?: string
}

export type UsePaginatorReturn = {
  hasMore: boolean
  remainingCount: number
  nextPageCount: number
  loadingMore: boolean
  loadMore: () => Promise<void>
}

export const usePaginator = <TVariables extends PaginatorVariables = any>(
  options: UsePaginatorOptions<TVariables>
): UsePaginatorReturn => {
  const { paginatorValue, query } = options
  const [loading, setLoading] = useState(false)

  const list = paginatorValue?.edges ?? []
  const total = paginatorValue?.total ?? 0
  const hasMore = paginatorValue?.pageInfo?.hasNextPage ?? false
  const remainingCount = Math.max(0, total - list.length)
  // Get the `first` variable from the query to determine next page count.
  const first = query.variables?.first || 0
  const nextPageCount = Math.min(remainingCount, first)

  const endCursor = paginatorValue?.pageInfo?.endCursor
  // Create paginator function that uses the variables
  const paginator = useCallback(() => {
    return query.fetchMore({
      variables: {
        ...query.variables,
        cursor: endCursor
      }
    })
  }, [endCursor, query])

  const loadMore = useCallback(async (): Promise<void> => {
    if (hasMore) {
      setLoading(true)
      await paginator()
      setLoading(false)
    }
  }, [hasMore, paginator])

  return useMemo(
    () => ({
      hasMore,
      remainingCount,
      nextPageCount,
      loadingMore: loading,
      loadMore
    }),
    [hasMore, remainingCount, nextPageCount, loading, loadMore]
  )
}

interface MutationConfig {
  name: string
  key: string
}

interface WithMutationPropsState {
  saving: boolean
  saved: boolean
  error: any
  result: any
}

export const withMutationProps =
  (mutationConfig: MutationConfig) =>
  (mutation: any, options: any) =>
  (C: ComponentType<any>) => {
    let mutateFunc: any = null

    class WithMutationProps extends Component<any, WithMutationPropsState> {
      static displayName = `WithMutationPropsFor_${mutationConfig.key}`

      state: WithMutationPropsState = {
        saving: false,
        saved: false,
        error: null,
        result: null
      }

      ComposedComponent = graphql(mutation, {
        ...options,
        props: (props: any) => {
          mutateFunc = props.mutate
          const passProps = options.props
            ? options.props({
                ...props,
                mutate: this.mutate
              })
            : {}
          return {
            ...passProps
          }
        }
      })(C)

      mutate = async (...args: any[]): Promise<any> => {
        this.setState({
          saving: true,
          saved: false,
          error: null
        })
        try {
          const result = await mutateFunc(...args)
          this.setState({
            result: get(result, `data.${mutationConfig.name}`) || result,
            saved: true
          })
          return result
        } catch (error) {
          this.setState({
            error: error
          })
        } finally {
          this.setState({
            saving: false
          })
        }
      }

      render(): ReactNode {
        return React.createElement(this.ComposedComponent, {
          ...this.props,
          [`${mutationConfig.key}_saving`]: this.state.saving,
          [`${mutationConfig.key}_saved`]: this.state.saved,
          [`${mutationConfig.key}_error`]: this.state.error,
          [`${mutationConfig.key}_result`]: this.state.result
        })
      }
    }

    return WithMutationProps
  }
